import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Container,
  Grid,
  Paper,
  Button,
  Chip,
  Avatar,
  Divider,
  Link,
  IconButton,
  Fade,
  Stack,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Description as DescriptionIcon,
  InsertDriveFile,
  PictureAsPdf as PictureAsPdfIcon,
  Download as DownloadIcon,
  CreditScore as CreditScoreIcon,
} from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";

export const CustomPage = ({ companyData, onLogout }) => {
  const navigate = useNavigate();

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };
  const API_BASE = import.meta.env.VITE_API_BASE;

  return (
    <Box sx={{ m: "auto", maxWidth: 950, borderRadius: 3, mb: 6 }}>
      <Container>
        {/* Company Info */}
        <Card elevation={3} sx={{ mb: 4, borderRadius: 4 }}>
          <CardContent sx={{ p: { xs: 2, md: 4 } }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "center", md: "space-between" },
                alignItems: "center",
                mb: 3,
                flexDirection: { xs: "column", md: "row" },
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: { xs: 1, md: 0 } }}>
                <Avatar sx={{ bgcolor: "#6366F1", width: 54, height: 54, mr: 2 }}>
                  <BusinessIcon sx={{ color: "#fff", fontSize: 32 }} />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight={700}>Company Information</Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    All your details at a glance
                  </Typography>
                </Box>
              </Box>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<CreditScoreIcon />}
                  onClick={() => navigate("/history")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Scanned Cards
                </Button>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => navigate("/company-details")}
                  sx={{
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  Edit Details
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={4}>
              {/* LEFT */}
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  {/* Username */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <PersonIcon sx={{ mr: 1, color: "#6366F1" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Username
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {companyData.username}
                    </Typography>
                  </Box>

                  {/* Company Name */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <BusinessIcon sx={{ mr: 1, color: "#6366F1" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Company Name
                      </Typography>
                    </Box>
                    <Typography variant="body1" fontWeight={600}>
                      {companyData.companyName}
                    </Typography>
                  </Box>

                  {/* Company URL */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <LanguageIcon sx={{ mr: 1, color: "#6366F1" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Company URL
                      </Typography>
                    </Box>
                    <Link
                      href={companyData.companyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        fontSize: "1rem",
                        fontWeight: 600,
                        color: "#6366F1",
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {companyData.companyUrl}
                    </Link>
                  </Box>
                </Stack>
              </Grid>

              {/* RIGHT */}
              <Grid item xs={12} md={6}>
                <Stack spacing={3}>
                  {/* Role */}
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Role
                    </Typography>
                    <Chip
                      label={companyData.role}
                      color="primary"
                      variant="filled"
                      sx={{ fontSize: "1rem", py: 1, px: 1.5, fontWeight: 600 }}
                    />
                  </Box>

                  {/* Description */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
                      <DescriptionIcon sx={{ mr: 1, color: "#6366F1" }} />
                      <Typography variant="subtitle2" color="text.secondary">
                        Description
                      </Typography>
                    </Box>
                    <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                      {companyData.description}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Documents Section */}
        {Array.isArray(companyData.uploaded_files) && companyData.uploaded_files.length > 0 && (
          <Card elevation={3} sx={{ borderRadius: 4 }}>
            <CardContent sx={{ p: { xs: 2, md: 4 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <InsertDriveFile sx={{ color: "#6366F1", mr: 1, fontSize: 28 }} />
                <Typography variant="h6" fontWeight={700}>
                  Uploaded Documents
                </Typography>
              </Box>
              <Grid container spacing={2}>
                {companyData.uploaded_files.map((file, index) => {
                  const safeFilename = file.filename || "";
                  const fileExt = safeFilename.split(".").pop()?.toLowerCase() || "";
                  const isImage = ["jpg", "jpeg", "png"].includes(fileExt);
                  const isPDF = fileExt === "pdf";
                  const fileUrl = `${API_BASE}/temp_data/${file.filename}`;
                  return (
                    <Grid item xs={12} md={6} key={index}>
                      <Paper
                        variant="outlined"
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          p: 2,
                          borderRadius: 3,
                          gap: 2,
                        }}
                      >
                        <Box sx={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 48,
                        }}>
                          {isImage ? (
                            <img
                              src={fileUrl}
                              alt={file.original_name}
                              style={{
                                width: 42,
                                height: 42,
                                objectFit: "cover",
                                borderRadius: 5,
                                marginRight: 12,
                                background: "#F3F4F6",
                                border: "1px solid #E0E7EF",
                              }}
                            />
                          ) : isPDF ? (
                            <PictureAsPdfIcon sx={{ color: "error.main", mr: 1, fontSize: 38 }} />
                          ) : (
                            <InsertDriveFile sx={{ color: "#64748b", mr: 1, fontSize: 36 }} />
                          )}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 500, mb: 0.25, whiteSpace: "nowrap", textOverflow: "ellipsis", overflow: "hidden" }}
                          >
                            {file.original_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {formatFileSize(file.size)}
                          </Typography>
                        </Box>
                        <IconButton
                          color="primary"
                          size="small"
                          component="a"
                          href={fileUrl}
                          download
                          target="_blank"
                          sx={{
                            ml: 1,
                            bgcolor: "#EEF2FF",
                            "&:hover": { bgcolor: "#6366F1", color: "#fff" },
                          }}
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </CardContent>
          </Card>
        )}
      </Container>
    </Box>
  );
};
